import React from 'react';
import {
	AppBar,
	Box,
	Divider,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	SwipeableDrawer,
	Toolbar,
	useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { NAVBAR } from 'data/navbar';
import { Link, NavLink } from 'react-router-dom';
import { SOCIAL } from 'data/socials';
import { SidebarButton } from 'components/Navbar/Sidebar';
import { MOBILE_BREAK_POINTS, SIDEBAR_WIDTH } from 'data/constant';

const FONT_STYLES = {
	fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
	fontSize: '14px',
	fontWeight: 500,
	letterSpacing: '0.2px',
} as const;

/*const StyledBox = styled('div')`
	display: flex;
	background-color: #030213;
	border-radius: 12px;
	padding: 7px 13px;
`;*/

const LogoButton = styled(Link as React.ComponentType<any>)`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 8px; /* larger touch target */
	text-decoration: none;
`;

const LogoImg = styled('img')`
	width: 40px;
	height: 40px;
`;

const DesktopOnly = styled('div')(({ theme }) => ({
	display: 'none',
	alignItems: 'center',
	height: '100%',
	[theme.breakpoints.up(700)]: {
		display: 'flex',
	},
}));

const MobileOnly = styled('div')(({ theme }) => ({
	display: 'flex',
	[theme.breakpoints.up(700)]: { display: 'none' },
	alignItems: 'center',
}));

const StyledNavLink = styled(NavLink)(({ theme }) => ({
	...FONT_STYLES,
	color: '#3A3A3A',
	margin: theme.spacing(1.25),
	cursor: 'pointer',
	textDecoration: 'none',
	'&:hover': { color: '#000' },
	'&.active': { color: '#2960F8' },
}));

const StyledAnchor = styled('a')(({ theme }) => ({
	...FONT_STYLES,
	color: '#3A3A3A',
	cursor: 'pointer',
	textDecoration: 'none',
	marginRight: theme.spacing(2),
	'&:hover': { color: '#000' },
}));

const SocialIcon = styled('img')`
	width: 24px;
	height: 24px;
	display: block;
`;

const SocialButton = styled('a')`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 44px; /* WCAG min touch target */
	height: 44px;
`;

const StyledAppBar = styled(AppBar)`
	position: fixed;
	width: calc(100vw - ${SIDEBAR_WIDTH}px);
	background: #fff;
	border-bottom: 1px solid #e2e2e2;
	top: 0;
	left: 0;

	@media (max-width: ${MOBILE_BREAK_POINTS}px) {
		width: 100vw;
	}
`;

const SocialWrapper = styled('nav')`
	display: flex;
	align-items: center;
	padding: 0 12px;
`;

const DrawerHeader = styled('div')`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 10px 14px;
	border-bottom: 1px solid #8a8c92;
`;

const DrawerContainer = styled(Box)`
	width: min(80vw, 360px);
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	height: 100%;
`;

const Column = styled('div')`
	display: flex;
	flex-direction: column;
	width: 100%;
`;

export const Navbar: React.FC = () => {
	const isMobile = useMediaQuery('(max-width:700px)');
	const [open, setOpen] = React.useState(false);
	const menuButtonRef = React.useRef<HTMLButtonElement | null>(null);

	React.useEffect(() => {
		if (!open) return;
		const handlePopState = () => setOpen(false);
		window.addEventListener('popstate', handlePopState);
		return () => window.removeEventListener('popstate', handlePopState);
	}, [open]);

	const handleOpen = React.useCallback(() => setOpen(true), []);
	const handleClose = React.useCallback(() => {
		setOpen(false);
		requestAnimationFrame(() => menuButtonRef.current?.focus());
	}, []);

	const DrawerContent = (
		<DrawerContainer role="presentation">
			<Column>
				<DrawerHeader>
					<LogoButton
						to="/"
						aria-label="Go to home"
						onClick={handleClose}
					>
						<img
							src="/images/rndm-logo.png"
							width={42}
							height={40}
							alt="Brand logo"
							loading="eager"
						/>
					</LogoButton>
					<IconButton
						aria-label="Close menu"
						onClick={handleClose}
						size="large"
					>
						<CloseIcon />
					</IconButton>
				</DrawerHeader>

				<nav aria-label="Primary">
					<List sx={{ py: 0 }}>
						{NAVBAR.map((d) => (
							<ListItem key={d.to} disablePadding>
								{d.target ? (
									<ListItemButton
										component="a"
										href={d.to}
										target={d.target}
										onClick={handleClose}
									>
										<ListItemText
											primaryTypographyProps={{
												...FONT_STYLES,
											}}
											primary={d.name}
										/>
									</ListItemButton>
								) : (
									<ListItemButton
										component={NavLink as any}
										to={d.to}
										onClick={handleClose}
									>
										<ListItemText
											primaryTypographyProps={{
												...FONT_STYLES,
											}}
											primary={d.name}
										/>
									</ListItemButton>
								)}
							</ListItem>
						))}
					</List>
				</nav>
			</Column>

			<Column>
				<Divider
					style={{ color: '#8a8c92', backgroundColor: '#8a8c92' }}
				/>

				<Box
					component="nav"
					aria-label="Social"
					sx={{
						display: 'flex',
						alignItems: 'center',
						px: 2,
						py: 1.5,
						gap: 1,
					}}
				>
					{SOCIAL.map((social) => (
						<SocialButton
							key={social.name}
							aria-label={social.name}
							href={social.url}
							target="_blank"
							rel="noopener noreferrer"
						>
							<SocialIcon
								src={social.icon}
								alt=""
								loading="lazy"
							/>
						</SocialButton>
					))}
				</Box>
			</Column>
		</DrawerContainer>
	);

	return (
		<>
			<StyledAppBar elevation={0}>
				<Toolbar
					disableGutters
					sx={{
						px: { xs: 1, sm: 2 },
						minHeight: 84,
						height: 84,
						width: '100%',
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						{/*<MobileOnly>
							<IconButton
								aria-label="Open menu"
								aria-controls={open ? 'mobile-menu' : undefined}
								aria-expanded={open ? 'true' : undefined}
								aria-haspopup="true"
								onClick={handleOpen}
								size="large"
								ref={menuButtonRef}
							>
								<MenuIcon />
							</IconButton>
						</MobileOnly>*/}

						<LogoButton to="/" aria-label="Go to home">
							{/*<StyledBox>
								<Typography
									color={'#fff'}
									variant={'subtitle1'}
									fontWeight={800}
									fontSize={20}
								>
									C
								</Typography>
							</StyledBox>
							<Typography
								fontSize={25}
								style={{ marginLeft: 10 }}
								fontWeight={700}
							>
								{upperCaseFirstChar(AGENT)}
							</Typography>*/}
							<LogoImg
								src="/images/rndm-logo.png"
								alt="Brand logo"
								loading="eager"
								width={42}
								height={35}
							/>
						</LogoButton>
					</Box>

					<Box
						sx={{
							marginLeft: 'auto',
							display: 'flex',
							alignItems: 'center',
						}}
					>
						<MobileOnly>
							<SidebarButton />
						</MobileOnly>

						<DesktopOnly
							sx={{
								flexGrow: 1,
								justifyContent: 'center',
							}}
						>
							<SocialWrapper
								aria-label="Primary navigation"
								sx={{
									width: '100%',
									justifyContent: 'center',
									display: 'flex',
								}}
							>
								{NAVBAR.map((d) =>
									d.target ? (
										<StyledAnchor
											key={d.to}
											target={d.target}
											href={d.to}
											rel="noopener noreferrer"
										>
											{d.name}
										</StyledAnchor>
									) : (
										<StyledNavLink key={d.to} to={d.to}>
											{d.name}
										</StyledNavLink>
									),
								)}

								<Box sx={{ width: 12 }} />
								<Box
									component="div"
									sx={{
										display: 'inline-flex',
										alignItems: 'center',
									}}
								>
									{SOCIAL.map((social) => (
										<SocialButton
											href={social.url}
											target="_blank"
											rel="noopener noreferrer"
											key={social.name}
											aria-label={social.name}
										>
											<SocialIcon
												src={social.icon}
												alt=""
												loading="lazy"
											/>
										</SocialButton>
									))}
								</Box>
							</SocialWrapper>
							{/*<ConnectButtonWrap>
								<SidebarButton />
							</ConnectButtonWrap>*/}
						</DesktopOnly>
					</Box>
				</Toolbar>
			</StyledAppBar>

			<SwipeableDrawer
				id="mobile-menu"
				anchor="left"
				open={isMobile && open}
				onOpen={handleOpen}
				onClose={handleClose}
				disableDiscovery={false}
				disableBackdropTransition={false}
				ModalProps={{ keepMounted: true }}
				PaperProps={{
					sx: {
						width: 'min(80vw, 360px)',
						pb: 'env(safe-area-inset-bottom)',
					},
					role: 'dialog',
					'aria-label': 'Mobile menu',
				}}
			>
				{DrawerContent}
			</SwipeableDrawer>

			<Box sx={{ height: 64 }} />
		</>
	);
};
